import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/toast/toast.js';
import '@awesome.me/webawesome/dist/components/toast-item/toast-item.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import type WaToast from '@awesome.me/webawesome/dist/components/toast/toast.js';
import type WaToastItem from '@awesome.me/webawesome/dist/components/toast-item/toast-item.js';
import type WaButton from '@awesome.me/webawesome/dist/components/button/button.js';

import '../ns-icon/ns-icon.js';
import { nsButtonStyles } from '../ns-styles/index.js';
import { TOAST_SHOWN_EVENT } from '../helpers/toast-events.js';

export interface Toast {
  label: string;
  description?: string;
  actionLabel?: string;
  action?: () => void;
  timeout?: number;
  variant?: 'success' | 'danger';
}

const DEFAULT_TIMEOUT_MS = 5000;
const MAX_VISIBLE_TOASTS = 3;
// How long after the user's own click on a guided action a toast counts as its answer
const ANSWER_WINDOW_MS = 5000;
const VARIANT_ICONS = { success: 'checkmark', danger: 'warning' } as const;

function toastKey(toast: Toast): string {
  return JSON.stringify([toast.variant ?? null, toast.label, toast.description ?? null]);
}

function element(tag: string, className: string, text?: string): HTMLElement {
  const el = document.createElement(tag);
  el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

@customElement('ltn-toast-service')
export class LtnToastService extends LitElement {
  @query('wa-toast')
  private __toaster?: WaToast;

  private __shown = new Map<string, { item: WaToastItem; count: number }>();

  // Set by the in-app guide while its lock is up: the notices are in the top layer, above its scrim, so
  // they're dimmed as the scrim dims the page and only their close button takes clicks (View would leave
  // the step). They're still announced.
  @property({ type: Boolean, reflect: true }) muted = false;

  private __answerUntil = 0;

  // The in-app guide calls this when the user clicks a step's action: a toast raised just after is the
  // page's answer to it (an error), so it stays usable while muted
  expectAnswer() {
    this.__answerUntil = Date.now() + ANSWER_WINDOW_MS;
  }

  // The guide's step moved on or ended, so nothing raised from now on answers that click
  endAnswer() {
    this.__answerUntil = 0;
  }

  static styles = [
    nsButtonStyles,
    css`
      wa-toast {
        --width: min(24rem, 100vw - var(--ns-toast-inline-start));
        --ns-toast-inline-start: calc(var(--ns-theme-viewport-padding, 40px) + var(--ns-side-bar-width, 75px));

        inset-inline-start: var(--ns-toast-inline-start);
      }

      wa-toast-item {
        --padding: var(--wa-space-s) var(--wa-space-m);
      }

      :host([muted]) wa-toast::part(stack) {
        pointer-events: none;
      }

      :host([muted]) wa-toast-item:not([data-answer]) {
        pointer-events: none;
        filter: brightness(50%);
      }

      :host([muted]) wa-toast-item:not([data-answer])::part(close-button) {
        pointer-events: auto;
      }

      wa-toast-item::part(toast-item) {
        background: var(--ns-theme-container);
        border: var(--wa-border-width-s) solid var(--ns-theme-grey-mid);
        border-radius: var(--wa-border-radius-l);
        overflow: hidden;
        box-shadow: var(--ns-elevation-8);
      }

      wa-toast-item[variant='danger']::part(toast-item) {
        border-color: var(--ns-theme-red);
      }

      wa-toast-item::part(accent) {
        display: none;
      }

      wa-toast-item ns-icon {
        --icon-width: 28px;
        --icon-height: 28px;
      }

      wa-toast-item[variant='danger'] ns-icon {
        background: color-mix(in srgb, var(--ns-theme-red) 20%, var(--ns-theme-white));
        color: var(--ns-theme-red);
      }

      wa-toast-item::part(content) {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--wa-space-xs);

        color: var(--ns-theme-type);
        font-size: var(--wa-font-size-s);
      }

      wa-toast-item::part(close-button) {
        color: color-mix(in srgb, var(--ns-theme-type) 60%, transparent);
      }

      wa-toast-item::part(progress-ring) {
        --track-color: transparent;
        --indicator-color: color-mix(in srgb, var(--ns-theme-purple) 35%, transparent);
        --indicator-width: 1px;
      }

      /* Overriding the ring's colour above also overrides Web Awesome's hiding of it on untimed toasts */
      wa-toast-item[variant='danger']::part(progress-ring) {
        --indicator-color: transparent;
      }

      .toast-text {
        display: flex;
        flex-direction: column;
        gap: var(--wa-space-3xs);
        min-width: 0;
      }

      .toast-title {
        font-weight: 600;
      }

      .toast-text:not(:has(.toast-description)) .toast-title {
        font-weight: inherit;
      }

      .toast-description {
        font-size: var(--wa-font-size-xs);
        opacity: 0.8;
      }

      .toast-count {
        opacity: 0.7;
      }

      .toast-separator {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        clip-path: inset(50%);
        white-space: nowrap;
      }

      wa-toast-item wa-button {
        --ns-button-min-width: auto;

        color: var(--ns-theme-purple);
        font-size: var(--wa-font-size-s);
        font-weight: 500;
      }
    `,
  ];

  async queueToast(toast: Toast) {
    await this.updateComplete;
    if (!this.__toaster) return;

    const key = toastKey(toast);
    const repeat = this.__shown.get(key);
    // A repeat replaces the one showing, so its count goes up and its timer starts again
    if (repeat) {
      repeat.item.dataset.leaving = '';
      repeat.item.hide();
    }

    const count = (repeat?.count ?? 0) + 1;
    const item = this.__buildItem(toast, count);
    if (Date.now() < this.__answerUntil) item.dataset.answer = '';
    this.__shown.set(key, { item, count });
    item.addEventListener('wa-after-hide', () => {
      if (this.__shown.get(key)?.item === item) this.__shown.delete(key);
    });

    this.__toaster.prepend(item);
    this.__hideOverflow();
    // The shutter, when it shows, lifts itself back above the toast's popover
    window.dispatchEvent(new CustomEvent(TOAST_SHOWN_EVENT));
  }

  private __buildItem(toast: Toast, count: number): WaToastItem {
    const item = document.createElement('wa-toast-item') as WaToastItem;
    if (toast.variant) item.variant = toast.variant;
    item.duration = toast.variant === 'danger' ? Infinity : (toast.timeout ?? DEFAULT_TIMEOUT_MS);

    if (toast.variant) {
      const icon = element('ns-icon', '');
      icon.setAttribute('icon', VARIANT_ICONS[toast.variant]);
      icon.setAttribute('slot', 'icon');
      icon.setAttribute('bubble', toast.variant === 'success' ? 'green' : '');
      icon.setAttribute('aria-hidden', 'true');
      item.append(icon);
    }

    const text = element('div', 'toast-text');
    const title = element('span', 'toast-title', toast.label);
    if (count > 1) title.append(element('span', 'toast-count', ` ×${count}`));
    text.append(title);
    if (toast.description) {
      // wa-toast announces the item's text, so the hidden stop keeps the title and description apart when read
      text.append(element('span', 'toast-separator', '. '), element('span', 'toast-description', toast.description));
    }
    item.append(text);

    if (toast.action) {
      const button = document.createElement('wa-button') as WaButton;
      button.appearance = 'plain';
      button.textContent = toast.actionLabel ?? 'Confirm';
      button.addEventListener('click', () => {
        toast.action?.();
        item.hide();
      });
      item.append(button);
    }

    return item;
  }

  private __hideOverflow() {
    const showing = [
      ...(this.__toaster?.querySelectorAll<WaToastItem>(':scope > wa-toast-item:not([data-leaving])') ?? []),
    ];
    for (const item of showing.slice(MAX_VISIBLE_TOASTS)) {
      item.dataset.leaving = '';
      item.hide();
    }
  }

  render() {
    return html`
      <wa-toast placement="bottom-start"></wa-toast>
    `;
  }
}
